import { useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { RootState } from "setup";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { selectConversation, selectCurrentChatSessionID, startNewChat } from "app/modules/aiAssistant/AIAssistantSlice";
import { loadChatSession } from "app/modules/aiAssistant/AIAssistantThunks";
import { ASSISTIMO_ROUTE, getAssistimoChatRoute } from "app/modules/aiAssistant/shared/assistimoRoutes";

// Keeps the URL and the open chat in step
export default function useSyncChatWithRoute() {
  const { chatSessionID: routeChatSessionID } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const thunkDispatch = useThunkDispatch();
  const store = useStore<RootState>();
  const openChatSessionID = useSelector(selectCurrentChatSessionID);
  const previousRouteChatSessionID = useRef(routeChatSessionID);

  useEffect(() => {
    // A chat that can't be opened, e.g. a deleted one, falls back to a new chat
    const openRouteChat = async (chatSessionID: string) => {
      const result = await thunkDispatch(loadChatSession(chatSessionID));
      const isStillOpen = selectConversation(store.getState()).key === result.meta.requestId;
      if (loadChatSession.rejected.match(result) && isStillOpen) navigate(ASSISTIMO_ROUTE, { replace: true });
    };

    const routeChanged = previousRouteChatSessionID.current !== routeChatSessionID;
    previousRouteChatSessionID.current = routeChatSessionID;

    if (routeChatSessionID) {
      if (routeChatSessionID !== openChatSessionID) openRouteChat(routeChatSessionID);
    } else if (openChatSessionID) {
      // The user has left the chat they had open
      if (routeChanged) dispatch(startNewChat());
      // A new chat has just been saved, or one was started in the pop-up
      else navigate(getAssistimoChatRoute(openChatSessionID), { replace: true });
    }
  }, [routeChatSessionID, openChatSessionID, store, dispatch, thunkDispatch, navigate]);
}
